import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { ReactComponent as FacebookIco } from 'assets/icons/facebook-icon.svg';
import { ReactComponent as InstagramIco } from 'assets/icons/inst-icon_white.svg';
import { ReactComponent as YoutubeIco } from 'assets/icons/youtube-icon.svg';
import { ReactComponent as PinterestIco } from 'assets/icons/pinterest-icon.svg';
import { ReactComponent as LinkedinIco } from 'assets/icons/linkedin-icon.svg';
import { ReactComponent as XingIco } from 'assets/icons/xing-icon.svg';
import { ReactComponent as PhoneIco } from 'assets/icons/phone-icon.svg';
import { ReactComponent as ChatIco } from 'assets/icons/chat-icon.svg';
import { ReactComponent as EmailIco } from 'assets/icons/at-icon.svg';

const types = {
  facebook: <FacebookIco />,
  phone: <PhoneIco />,
  instagram: <InstagramIco />,
  youtube: <YoutubeIco />,
  pinterest: <PinterestIco />,
  linkedin: <LinkedinIco />,
  xing: <XingIco />,
  chat: <ChatIco />,
  email: <EmailIco />,
};

export default function SocialLink({ type, url, urlSuffix, socialIcon, className, svgColor, textColor, isInline }) {
  function renderSocialLink(type) {
    const socialIconUrl = socialIcon?.file?.url;

    const renderIcon = () => {
      if (isInline) return;

      return (
        <StyledIcon svgColor={svgColor}>
          {socialIconUrl ? (
            <img src={socialIconUrl} width="17" height="18" alt="social icon" loading="lazy" />
          ) : (
            types[type]
          )}
        </StyledIcon>
      );
    };

    const renderLink = (text, conditional) => {
      const target = conditional ? '_self' : '_blank';
      const rel = conditional ? '' : 'noopener noreferrer';

      return (
        <a
          href={url}
          aria-label={type.charAt(0).toUpperCase() + type.slice(1)}
          target={target}
          rel={rel}
          className={className}
        >
          {text}
        </a>
      );
    };

    const isTel = url && url.length > 0 && url.substring(0, 3) === 'tel';
    const isMailto = url && url.length > 0 && url.substring(0, 6) === 'mailto';

    const defaultLinkTarget = isMailto ? '_self' : '_blank';
    const defaultLinkRel = isMailto ? '' : 'noopener noreferrer';

    switch (type) {
      case 'phone':
        return (
          <SocialLinkWrapper textColor={textColor} isInline={isInline}>
            {renderIcon()}
            <StyledLinkWrapper>
              {renderLink(url.replace('tel:', ''), isTel)}
              {urlSuffix && (
                <>
                  &nbsp;
                  <span>{urlSuffix}</span>
                </>
              )}
            </StyledLinkWrapper>
          </SocialLinkWrapper>
        );
      case 'email':
        return (
          <SocialLinkWrapper textColor={textColor} isInline={isInline}>
            {renderIcon()}
            <StyledLinkWrapper>{renderLink(url.replace('mailto:', ''), isMailto)}</StyledLinkWrapper>
          </SocialLinkWrapper>
        );
      case 'chat':
        return (
          <SocialLinkWrapper textColor={textColor} isInline={isInline}>
            {renderIcon()}
            <StyledLinkWrapper>{renderLink('Chat', isTel)}</StyledLinkWrapper>
          </SocialLinkWrapper>
        );
      default:
        return types && types[type] ? (
          <a
            href={url}
            aria-label={type.charAt(0).toUpperCase() + type.slice(1)}
            target={defaultLinkTarget}
            rel={defaultLinkRel}
            className={className}
          >
            {renderIcon()}
          </a>
        ) : (
          <p>Unknown type</p>
        );
    }
  }

  return renderSocialLink(type);
}

const dynamicSvgColor = ({ svgColor }) =>
  svgColor &&
  css`
    stroke: ${svgColor};
  `;

const StyledIcon = styled.span`
  display: flex;
  align-items: center;

  svg {
    stroke: currentColor;
    ${dynamicSvgColor};
  }
`;

const StyledLinkWrapper = styled.span`
  margin-left: 10px;
`;

const dynamicTextColor = ({ textColor }) =>
  textColor &&
  css`
    color: ${textColor || 'currentColor'};
  `;

const dynamicInline = ({ isInline }) =>
  isInline &&
  css`
    display: inline;
    margin: 0 4px;

    span {
      margin-left: 0;
    }
  `;

const SocialLinkWrapper = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  ${dynamicTextColor};
  ${dynamicInline};
`;
